import { useState } from 'react';
import { AlertIcon } from 'taboola-ultimate-ui';
import { TYPE_INDICATORS } from 'modules/PixelTester/config/pixelTesterConsts';
import { countUserMatchingIssues } from 'modules/PixelTester/utils/utils';
import { COMMON_FLAGS, useConfigMatch } from 'modules/taboola-common-frontend-modules/account-configurations';
import { FormattedMessage } from 'modules/taboola-common-frontend-modules/i18n';
import { CollapsibleView } from '../CollapsibleView/CollapsibleView';
import { DetailRow } from '../DetailRow/DetailRow';
import { UserMatchingRow } from '../UserMatchingRow/UserMatchingRow';
import styles from './userMatching.module.scss';

export const UserMatching = ({ userMatchingData, hideCookieConsentData, userMatchingIssueType }) => {
    const [collapsed, setCollapsed] = useState(true);
    const isUserConsentEnabled = useConfigMatch(
        {},
        {
            [COMMON_FLAGS.PIXEL_TESTER_SHOW_USER_MATCHING_CONSENT]: 'true',
        }
    );
    const issueCount = countUserMatchingIssues(userMatchingData, hideCookieConsentData, isUserConsentEnabled);
    const showCollapsedIcon = collapsed && userMatchingIssueType !== TYPE_INDICATORS.OK;
    const showUserConsent = !hideCookieConsentData && isUserConsentEnabled;

    return (
        <DetailRow labelId="pixel.tester.event.matching.title" defaultLabel="User Matching">
            <div className={styles['user-matching-container']}>
                <div className={styles['user-matching-row']}>
                    {showCollapsedIcon && (
                        <AlertIcon
                            className={
                                styles[userMatchingIssueType === TYPE_INDICATORS.ALERT ? 'alert-icon' : 'error-icon']
                            }
                        />
                    )}
                    {issueCount === 0 ? (
                        <FormattedMessage id="pixel.tester.event.matching.no.issues" defaultMessage="Success" />
                    ) : issueCount === 1 ? (
                        <FormattedMessage id="pixel.tester.events.header.issues.single" defaultMessage="1 Issue" />
                    ) : (
                        <FormattedMessage
                            id="pixel.tester.events.header.issues.multiple"
                            defaultMessage="{issueCount} Issues"
                            values={{ issueCount }}
                        />
                    )}
                </div>

                <CollapsibleView collapsed={collapsed} setCollapsed={setCollapsed}>
                    <UserMatchingRow
                        indicator={userMatchingData?.clickId}
                        tooltipContent={
                            !userMatchingData?.clickId && (
                                <div className={styles['tooltip-text']}>
                                    <FormattedMessage
                                        id="pixel.tester.event.matching.tblci.tooltip.text"
                                        defaultMessage="Click ID is essential for conversion tracking in cookieless environments (e.g. Safari, iOS). If you're experiencing problems, consider the following:{br}{br}
                                                        <ul><li><b>Cross-Domain Tracking</b>: If your funnel involves multiple domains, ensure the Click ID parameter is correctly passed between them.</li>
                                                        <li><b>Parameter Integrity</b>: Verify that the tblci parameter is not being modified or stripped by your website or any intermediate systems.</li></ul>{br}"
                                    />
                                    <FormattedMessage
                                        id="pixel.tester.event.matching.tooltip.dev.center.link"
                                        defaultMessage="Check out our <a>Dev Center</a> for more information.{br}"
                                    />
                                    <FormattedMessage
                                        id="pixel.tester.event.matching.tooltip.support.mail.link"
                                        defaultMessage="Reach out to <a>support@taboola.com</a> for technical help."
                                    />
                                </div>
                            )
                        }
                    >
                        <FormattedMessage
                            id="pixel.tester.event.matching.tblci"
                            defaultMessage="Taboola  Click ID (tblci){separator}"
                            values={{ separator: !userMatchingData?.clickId ? ' - ' : '' }}
                        />
                        {!userMatchingData?.clickId && (
                            <FormattedMessage
                                id="pixel.tester.event.matching.troubleshoot"
                                defaultMessage="Troubleshoot"
                                className={styles['troubleshoot-link']}
                            />
                        )}
                    </UserMatchingRow>
                    {!hideCookieConsentData && (
                        <UserMatchingRow
                            indicator={userMatchingData?.cookieId}
                            tooltipContent={
                                !userMatchingData?.cookieId ? (
                                    <div className={styles['tooltip-text']}>
                                        <FormattedMessage
                                            id="pixel.tester.event.matching.cookie.tooltip.text"
                                            defaultMessage="Cookie ID is vital for effective retargeting and maximizing conversion match rates. If you're experiencing problems, consider the following:{br}{br}
                                                            <ul><li><b>Browser Settings</b>: Verify that your browser permits third-party cookies and. Chrome typically does, whereas Safari and iOS often block them.</li>
                                                            <li><b>Cookie Consent</b>: If a cookie banner is present, make sure you have accepted cookies.</li>
                                                            <li><b>Ad Blockers</b>: Disable any active ad blockers.</li>
                                                            <li><b>Incognito/Private Mode</b>: These browsing modes often block third-party cookies by default. For accurate testing, use a standard browsing session.</li></ul>{br}"
                                        />
                                        <FormattedMessage
                                            id="pixel.tester.event.matching.tooltip.dev.center.link"
                                            defaultMessage="Check out our <a>Dev Center</a> for more information.{br}"
                                        />
                                        <FormattedMessage
                                            id="pixel.tester.event.matching.tooltip.support.mail.link"
                                            defaultMessage="Reach out to <a>support@taboola.com</a> for technical help."
                                        />
                                    </div>
                                ) : (
                                    userMatchingData?.cookieId
                                )
                            }
                        >
                            <FormattedMessage
                                id="pixel.tester.event.matching.cookie"
                                defaultMessage="Cookie ID{separator}"
                                values={{ separator: !userMatchingData?.cookieId ? ' - ' : '' }}
                            />
                            {!userMatchingData?.cookieId && (
                                <FormattedMessage
                                    id="pixel.tester.event.matching.troubleshoot"
                                    defaultMessage="Troubleshoot"
                                    className={styles['troubleshoot-link']}
                                />
                            )}
                        </UserMatchingRow>
                    )}
                    {showUserConsent && (
                        <UserMatchingRow indicator={userMatchingData?.isUserConsent}>
                            <FormattedMessage
                                id="pixel.tester.event.matching.consent"
                                defaultMessage="Consent Signal"
                            />
                        </UserMatchingRow>
                    )}
                </CollapsibleView>
            </div>
        </DetailRow>
    );
};
